import React, { useState, useEffect } from 'react';
import { Warning } from '@mui/icons-material';
import { DynamicContainer } from '../components/common/DynamicContainer';
import { generateBreadcrumbPath } from '../components/common/breadcrumbs';
import { toast } from 'react-toastify';
import { 
  IconButton, 
  Tooltip,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Badge,
  Typography,
} from '@mui/material';
import { CheckCircle } from '@mui/icons-material';

const API_BASE_URL = `${process.env.REACT_APP_BACKEND_URL}synctrack/agent/alerts`;

const token = localStorage.getItem('token');
const tenantId = localStorage.getItem('tenantId');

const AgentAlerts = () => {
  const [alerts, setAlerts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('OPEN');
  const [severityFilter, setSeverityFilter] = useState('ALL');

  useEffect(() => {
    fetchAlerts();
  }, [statusFilter, severityFilter]);

  const fetchAlerts = async () => {
    setIsLoading(true);
    try {
      let url = `${API_BASE_URL}/tenant/${tenantId}`;
      if (statusFilter === 'OPEN') {
        url = `${API_BASE_URL}/tenant/${tenantId}/open`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      let filteredData = data;

      if (severityFilter !== 'ALL') {
        filteredData = data.filter(alert => alert.severity === severityFilter);
      }

      const mappedData = filteredData.map((item) => ({
        id: item.alertId,
        alertId: item.alertId,
        agentId: item.agentId,
        alertType: item.alertType,
        severity: item.severity,
        entity: item.entity || 'N/A',
        message: item.message,
        status: item.status,
        createdAt: item.createdAt,
      }));

      setAlerts(mappedData);
    } catch (error) {
      console.error('Error fetching alerts:', error);
      toast.error(`Error loading alerts: ${error.message}`);
      setAlerts([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseAlert = async (alertId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/${alertId}/close`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      toast.success('Alert closed successfully');
      await fetchAlerts();
    } catch (error) {
      console.error('Error closing alert:', error);
      toast.error(`Error closing alert: ${error.message}`);
    }
  };

  const formatDateTime = (dateTime) => {
    if (!dateTime) return 'N/A';
    try {
      const date = new Date(dateTime);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (e) {
      return 'Invalid date';
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'LOW':
        return 'default';
      case 'MEDIUM':
        return 'info';
      case 'HIGH':
        return 'warning';
      case 'CRITICAL':
        return 'error';
      default:
        return 'default';
    }
  };

  const openAlertsCount = alerts.filter(a => a.status === 'OPEN').length;

  const columns = [
    { field: 'alertId', headerName: 'Alert ID', width: 120, renderCell: (params) => (
      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
        {params.value?.substring(0, 8)}...
      </Typography>
    )},
    { field: 'agentId', headerName: 'Agent', width: 150 },
    { field: 'alertType', headerName: 'Alert Type', width: 150 },
    { 
      field: 'severity', 
      headerName: 'Severity', 
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={getSeverityColor(params.value)}
          size="small"
        />
      ),
    },
    { field: 'entity', headerName: 'Entity', width: 150 },
    { 
      field: 'message', 
      headerName: 'Message', 
      width: 300,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ 
          overflow: 'hidden', 
          textOverflow: 'ellipsis', 
          whiteSpace: 'nowrap',
          maxWidth: '100%'
        }}>
          {params.value}
        </Typography>
      ),
    },
    { 
      field: 'status', 
      headerName: 'Status', 
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={params.value === 'OPEN' ? 'warning' : 'default'}
          size="small"
        />
      ),
    },
    { 
      field: 'createdAt', 
      headerName: 'Created At', 
      width: 180,
      renderCell: (params) => formatDateTime(params.value),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        params.row.status === 'OPEN' ? (
          <Tooltip title="Close Alert">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleCloseAlert(params.row.alertId);
              }}
              color="success"
            >
              <CheckCircle />
            </IconButton>
          </Tooltip>
        ) : null
      ),
    },
  ];

  const breadcrumbPaths = generateBreadcrumbPath('Agent Alerts');

  return (
    <>
      <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <Badge badgeContent={openAlertsCount} color="error">
          <Warning color="action" />
        </Badge>
        <Typography variant="body2" color="text.secondary">
          {openAlertsCount} open alert{openAlertsCount !== 1 ? 's' : ''}
        </Typography>

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="ALL">All Statuses</MenuItem>
            <MenuItem value="OPEN">Open</MenuItem>
            <MenuItem value="CLOSED">Closed</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Severity</InputLabel>
          <Select
            value={severityFilter}
            label="Severity"
            onChange={(e) => setSeverityFilter(e.target.value)}
          >
            <MenuItem value="ALL">All Severities</MenuItem>
            <MenuItem value="LOW">Low</MenuItem>
            <MenuItem value="MEDIUM">Medium</MenuItem>
            <MenuItem value="HIGH">High</MenuItem>
            <MenuItem value="CRITICAL">Critical</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <DynamicContainer
        title="Agent Alerts"
        icon={Warning}
        columns={columns}
        formFields={[]}
        initialFormData={{}}
        idField="alertId"
        breadcrumbPaths={breadcrumbPaths}
        rows={alerts}
        onCreate={null}
        onUpdate={null}
        onDelete={null}
        isLoading={isLoading}
        disableEdit={true}
      />
    </>
  );
};

export default AgentAlerts;
